import React, { createContext, useContext, useState, useCallback } from "react";

// dialogContext — sustituye a los alert()/window.confirm() nativos del
// navegador por ventanas emergentes con el mismo estilo que el resto de la
// app (misma clase .modal que usan todos los demás popups). Se usa como:
//   const { avisar, confirmar } = useDialog();
//   avisar("Mensaje de aviso");                    // como alert()
//   if (await confirmar("¿Seguro?")) { ... }        // como window.confirm()
//   if (await confirmarPeligro("¿Eliminar?", { titulo, boton })) { ... }
//     -> igual que confirmar, pero ventana roja con alarma sonora, para
//        acciones que no se pueden deshacer (p.ej. eliminar una ruta).
//   if (await alertaRevision("Motivo…", { titulo, pregunta, boton, desmarcar })) { ... }
//     -> aviso AMARILLO con alarma: llave o piso marcado para revisión al
//        prestarla, devolverla o prepararla. Cancelar / seguir igualmente, y
//        interruptores para desmarcar (desmarcar = [{clave, texto, hecho}]).
//        Resuelve {seguir, elegidos:[claves activadas]}. Ver revision.js >
//        pedirConfirmacionRevision.
//   exito("Todo bien")
//     -> aviso VERDE flotante que se cierra solo (no hay que pulsar nada).
const DialogContext = createContext();

// Pitido de alarma corto (dos tonos alternos) con Web Audio: sin ficheros de
// sonido. Si el navegador lo bloquea, simplemente no suena.
// Alarma de "atención" (más suave que la de peligro): tres pitidos medios.
function sonarAtencion() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "triangle";
    osc.frequency.value = 740;
    osc.connect(gain).connect(ctx.destination);
    const t0 = ctx.currentTime;
    gain.gain.setValueAtTime(0, t0);
    for (let i = 0; i < 3; i++) {
      gain.gain.setValueAtTime(0.18, t0 + i * 0.28);
      gain.gain.setValueAtTime(0, t0 + i * 0.28 + 0.16);
    }
    osc.start(t0);
    osc.stop(t0 + 0.9);
    osc.onended = () => ctx.close();
  } catch { /* sin audio */ }
}

function sonarAlarma() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "square";
    gain.gain.value = 0.08;
    osc.connect(gain).connect(ctx.destination);
    const t0 = ctx.currentTime;
    for (let i = 0; i < 6; i++) osc.frequency.setValueAtTime(i % 2 ? 660 : 880, t0 + i * 0.18);
    osc.start(t0);
    osc.stop(t0 + 6 * 0.18);
    osc.onended = () => ctx.close();
  } catch { /* sin audio */ }
}

export function DialogProvider({ children }) {
  const [dialogo, setDialogo] = useState(null); // { tipo, mensaje, resolve }
  const [toasts, setToasts] = useState([]);      // avisos verdes de "todo bien"

  const exito = useCallback((mensaje, ms = 4500) => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, mensaje }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), ms);
  }, []);

  const avisar = useCallback((mensaje) => {
    return new Promise((resolve) => setDialogo({ tipo: "alert", mensaje, resolve }));
  }, []);

  const confirmar = useCallback((mensaje) => {
    return new Promise((resolve) => setDialogo({ tipo: "confirm", mensaje, resolve }));
  }, []);

  const confirmarPeligro = useCallback((mensaje, { titulo = "¡Atención!", boton = "Eliminar" } = {}) => {
    sonarAlarma();
    return new Promise((resolve) => setDialogo({ tipo: "peligro", mensaje, titulo, boton, resolve }));
  }, []);

  const alertaRevision = useCallback((mensaje, { titulo = "Llave marcada para revisión", pregunta = "¿Quieres seguir adelante?",
                                                 boton = "Sí, seguir", desmarcar = [] } = {}) => {
    sonarAtencion();
    return new Promise((resolve) => setDialogo({ tipo: "atencion", mensaje, titulo, pregunta, boton, resolve,
                                                 desmarcar: desmarcar.map((d) => ({ ...d, activo: false })) }));
  }, []);

  // Interruptor "Desmarcar" del aviso amarillo: solo cambia la elección; se
  // aplica al confirmar la operación (ver revision.js).
  const alternarDesmarcar = (i) => setDialogo((d) => d && d.tipo === "atencion"
    ? { ...d, desmarcar: d.desmarcar.map((x, j) => (j === i ? { ...x, activo: !x.activo } : x)) } : d);
  const cerrarAtencion = (seguir) => {
    dialogo?.resolve({ seguir, elegidos: seguir ? dialogo.desmarcar.filter((x) => x.activo).map((x) => x.clave) : [] });
    setDialogo(null);
  };

  const cerrar = (valor) => {
    dialogo?.resolve(valor);
    setDialogo(null);
  };

  return (
    <DialogContext.Provider value={{ avisar, confirmar, confirmarPeligro, alertaRevision, exito }}>
      {children}
      {toasts.length > 0 && (
        <div className="toasts-exito" role="status" aria-live="polite">
          {toasts.map((t) => (
            <div key={t.id} className="toast-exito"><span className="te-ico">✅</span><span>{t.mensaje}</span></div>
          ))}
        </div>
      )}
      {dialogo?.tipo === "peligro" && (
        <div className="modal-back modal-back-peligro">
          <div className="modal modal-sm modal-peligro" onMouseDown={(e) => e.stopPropagation()}>
            <h3>🚨 {dialogo.titulo}</h3>
            <p style={{ whiteSpace: "pre-line" }}>{dialogo.mensaje}</p>
            <div className="modal-actions">
              <button className="btn" onClick={() => cerrar(false)} autoFocus>Cancelar</button>
              <button className="btn btn-danger" onClick={() => cerrar(true)}>{dialogo.boton}</button>
            </div>
          </div>
        </div>
      )}
      {dialogo?.tipo === "atencion" && (
        <div className="modal-back modal-back-atencion">
          <div className="modal modal-sm modal-atencion" onMouseDown={(e) => e.stopPropagation()}>
            <h3>🚩 {dialogo.titulo}</h3>
            <p style={{ whiteSpace: "pre-line" }}>{dialogo.mensaje}</p>
            {dialogo.desmarcar.length > 0 && (
              <div className="atencion-desmarcar">
                {dialogo.desmarcar.map((d, i) => (
                  <button key={i} type="button" className={`btn-desmarcar ${d.activo ? "activo" : ""}`}
                          aria-pressed={d.activo} onClick={() => alternarDesmarcar(i)}>
                    {d.activo
                      ? <>✅ {d.hecho}<small>pulsa para mantener la marca</small></>
                      : <>🏳️ {d.texto}<small>se aplica al confirmar</small></>}
                  </button>
                ))}
              </div>
            )}
            <p className="atencion-pregunta">{dialogo.pregunta}</p>
            <div className="modal-actions">
              <button className="btn" onClick={() => cerrarAtencion(false)} autoFocus>Cancelar</button>
              <button className="btn btn-atencion" onClick={() => cerrarAtencion(true)}>{dialogo.boton}</button>
            </div>
          </div>
        </div>
      )}
      {dialogo && dialogo.tipo !== "peligro" && dialogo.tipo !== "atencion" && (
        <div className="modal-back" onMouseDown={() => dialogo.tipo === "alert" && cerrar(true)}>
          <div className="modal modal-sm" onMouseDown={(e) => e.stopPropagation()}>
            <p style={{ marginTop: 0, whiteSpace: "pre-line" }}>{dialogo.mensaje}</p>
            <div className="modal-actions">
              {dialogo.tipo === "confirm" && (
                <button className="btn" onClick={() => cerrar(false)}>Cancelar</button>
              )}
              <button className="btn btn-primary" onClick={() => cerrar(true)} autoFocus>
                {dialogo.tipo === "confirm" ? "Confirmar" : "Aceptar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </DialogContext.Provider>
  );
}

export function useDialog() {
  return useContext(DialogContext);
}
